import {
  DataGrid,
  FilterToolbar,
  StatusBadge,
  TrackRate,
  type FilterDef,
  type FilterOption,
  type FilterValues,
  type GridQuery,
  type Severity
} from '@ijoin/components';
import {
  GetAdminParticipantsParams,
  GetAdminParticipantsRes,
  useGetAdminParticipants
} from '@ijoin/hooks';
import { Stack } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { keepPreviousData } from '@tanstack/react-query';

import { FC, useState } from 'react';

import { INVESTMENT_VEHICLE_CONTRIBUTION_UNIT_TYPES } from '../consts';
import { LinkButton } from './LinkButton';
import { ParticipantActionsCell } from './ParticipantActionsCell';

const YES_NO_NA_OPTIONS: FilterOption[] = [
  { label: 'Yes', value: '1' },
  { label: 'No', value: '0' },
  { label: 'N/A', value: 'null' }
];

const GENDER_OPTIONS: FilterOption[] = [
  { label: 'Female', value: 'F' },
  { label: 'Male', value: 'M' }
];

const EMPLOYMENT_STATUS_OPTIONS: FilterOption[] = [
  { label: 'Active', value: 'a7ce6741-09fc-4cbb-8f1c-181f0414c203' },
  { label: 'Deceased', value: '2aaae56d-93e9-4ea4-885d-a1d830e23bf4' },
  { label: 'Disabled', value: '224ba768-a734-4b53-953b-78d56b0d2de1' },
  { label: 'Inactive', value: '48e1fe6e-0c8c-4c41-81f0-7f554f6b75f3' },
  { label: 'Leave of Absence', value: 'e11b65c5-8bd6-4e80-bdbe-b87b3cd87be4' },
  { label: 'Retired', value: 'c7294cc7-e729-43dc-90f7-c3198dab3dbc' },
  { label: 'Terminated', value: '8b5cb2da-b84e-4c31-8621-db8cd22e0f86' }
];

const PLAN_STATUS_OPTIONS: FilterOption[] = [
  { label: 'Active', value: '8a3a5274-73ea-4fac-bd0a-ce4ed68a7fb3' },
  {
    label: 'Eligible Not Contributing',
    value: '326a2bd1-d5d7-47cb-a983-bcd6729e7861'
  },
  { label: 'Inactive', value: '1a322d7e-58f5-49c1-897d-09135e2a3c3d' },
  { label: 'Ineligible', value: 'c4157a6f-5af1-46f9-bf5c-00f749e700b1' },
  { label: 'Unknown', value: '3ee8adba-0984-4cae-a5b7-1797d4003a3c' }
];

const MAP_STATUS_OPTIONS: FilterOption[] = [
  { label: 'Auto Re-enroll', value: 'A' },
  { label: 'Defaulted', value: 'D' },
  { label: 'Opted Out', value: 'O' },
  { label: 'Plan-level', value: 'P' },
  { label: 'Voluntary', value: 'V' },
  { label: 'N/A', value: 'null' }
];

const EMPLOYMENT_STATUS_SEVERITY: Record<string, Severity> = {
  Active: 'success',
  Deceased: 'error',
  Disabled: 'warning',
  Inactive: 'warning',
  'Leave of Absence': 'warning',
  Retired: 'info',
  Terminated: 'error'
};

const PLAN_STATUS_SEVERITY: Record<string, Severity> = {
  Active: 'success',
  Ineligible: 'error'
};

const MAP_ENTRY_TYPE_LABELS: Record<string, string> = {
  A: 'Auto Re-enroll',
  D: 'Defaulted',
  O: 'Opted Out',
  P: 'Plan-level',
  V: 'Voluntary'
};

const NUMERIC = { align: 'right', headerAlign: 'right' } as const;

export const ParticipantsGrid: FC<{
  advisoryOrganizationId?: string;
  /** lets the global route refetch plan options for the drafted org */
  onDraftChange?: (values: FilterValues) => void;
  /** org filter options, supplied only by the global route */
  orgOptions?: FilterOption[];
  planId?: string;
  /** plan filter options, supplied by the org and global routes */
  planOptions?: FilterOption[];
}> = props => {
  const [query, setQuery] = useState<GridQuery>();

  const participants = useGetAdminParticipants(
    {
      ...(query as GetAdminParticipantsParams),
      ...(props.advisoryOrganizationId && {
        advisoryOrganizationId: props.advisoryOrganizationId
      }),
      ...(props.planId && { planId: props.planId })
    },
    {
      query: {
        enabled: !!query,
        placeholderData: keepPreviousData
      }
    }
  );

  const testId = props.planId
    ? 'planParticipants'
    : props.advisoryOrganizationId
      ? 'participants'
      : 'allParticipants';

  const filters: FilterDef[] = [
    { label: 'Last Name', name: 'lastName', type: 'text' },
    { label: 'First Name', name: 'firstName', type: 'text' },
    { label: 'SSN', name: 'socialSecurityNumber', type: 'text' },
    ...(props.orgOptions
      ? [
          {
            label: 'Advisory Organization',
            name: 'advisoryOrganizationId',
            options: props.orgOptions,
            type: 'autocomplete' as const
          }
        ]
      : []),
    ...(props.planId
      ? []
      : [
          {
            label: 'Plan',
            name: 'planId',
            options: props.planOptions ?? [],
            type: 'autocomplete' as const
          }
        ]),
    {
      endName: 'endAge',
      label: 'Age',
      startName: 'startAge',
      type: 'numberRange'
    },
    {
      endName: 'endOnTrackRate',
      label: 'Goal Completion',
      startName: 'startOnTrackRate',
      type: 'numberRange'
    },
    {
      endName: 'endExternalTotalPlanBalance',
      format: 'currency',
      label: 'Balance',
      startName: 'startExternalTotalPlanBalance',
      type: 'numberRange'
    },
    {
      endName: 'endOutsideAssets',
      format: 'currency',
      label: 'Outside Assets',
      startName: 'startOutsideAssets',
      type: 'numberRange'
    },
    {
      endName: 'endAnnualSalary',
      format: 'currency',
      label: 'Salary',
      startName: 'startAnnualSalary',
      type: 'numberRange'
    },
    {
      endName: 'endCurrentTotalContributionPercent',
      label: 'Savings Rate',
      startName: 'startCurrentTotalContributionPercent',
      type: 'numberRange'
    },
    {
      label: 'Match Maximized',
      name: 'isMaximizingERMatch',
      options: YES_NO_NA_OPTIONS,
      type: 'select'
    },
    {
      label: 'Gender',
      name: 'gender',
      options: GENDER_OPTIONS,
      type: 'select'
    },
    {
      endName: 'endFundElections',
      label: 'Fund Elections',
      startName: 'startFundElections',
      type: 'numberRange'
    },
    {
      endName: 'endCurrentPreTaxContributionPercent',
      label: 'Pre-tax (%)',
      startName: 'startCurrentPreTaxContributionPercent',
      type: 'numberRange'
    },
    {
      endName: 'endCurrentPostTaxContributionPercent',
      label: 'Roth (%)',
      startName: 'startCurrentPostTaxContributionPercent',
      type: 'numberRange'
    },
    {
      endName: 'endCurrentPreTaxContributionDollars',
      format: 'currency',
      label: 'Pre-tax ($)',
      startName: 'startCurrentPreTaxContributionDollars',
      type: 'numberRange'
    },
    {
      endName: 'endCurrentPostTaxContributionDollars',
      format: 'currency',
      label: 'Roth ($)',
      startName: 'startCurrentPostTaxContributionDollars',
      type: 'numberRange'
    },
    {
      endName: 'endLdiReturnRate',
      label: 'Return Rate',
      startName: 'startLdiReturnRate',
      type: 'numberRange'
    },
    {
      label: 'MAP Status',
      name: 'entryExitTypeCode',
      options: MAP_STATUS_OPTIONS,
      type: 'select'
    },
    {
      endName: 'endEligibleOn',
      label: 'Eligible On',
      startName: 'startEligibleOn',
      type: 'dateRange'
    },
    {
      label: 'Employment Status',
      name: 'employmentStatusId',
      options: EMPLOYMENT_STATUS_OPTIONS,
      type: 'select'
    },
    {
      label: 'Plan Status',
      name: 'planParticipationStatusId',
      options: PLAN_STATUS_OPTIONS,
      type: 'select'
    },
    { label: 'Home Email Address', name: 'personalEmail', type: 'text' },
    { label: 'Work Email Address', name: 'workEmail', type: 'text' },
    { label: 'Work Phone', name: 'workPhone', type: 'text' },
    { label: 'Cell Phone', name: 'cellPhone', type: 'text' },
    {
      endName: 'endLastEnrollmentDate',
      label: 'Last Enrollment',
      startName: 'startLastEnrollmentDate',
      type: 'dateRange'
    },
    {
      endName: 'endLastLogin',
      label: 'Last Login',
      startName: 'startLastLogin',
      type: 'dateRange'
    },
    {
      endName: 'endLastUpdated',
      label: 'Last Updated',
      startName: 'startLastUpdated',
      type: 'dateRange'
    }
  ];

  const columns: GridColDef<GetAdminParticipantsRes>[] = [
    {
      field: 'lastName',
      flex: 1,
      headerName: 'Last Name',
      minWidth: 140,
      renderCell: params => (
        <LinkButton
          params={{
            advisoryOrganizationId: params.row.advisoryOrganizationId,
            planEnrollmentId: params.row.planEnrollmentId,
            planId: params.row.planId
          }}
          to='/organizations/$advisoryOrganizationId/plans/$planId/participants/$planEnrollmentId'
          variant='text'>
          {params.value}
        </LinkButton>
      )
    },
    {
      field: 'firstName',
      flex: 1,
      headerName: 'First Name',
      minWidth: 140,
      renderCell: params => (
        <LinkButton
          params={{
            advisoryOrganizationId: params.row.advisoryOrganizationId,
            planEnrollmentId: params.row.planEnrollmentId,
            planId: params.row.planId
          }}
          to='/organizations/$advisoryOrganizationId/plans/$planId/participants/$planEnrollmentId'
          variant='text'>
          {params.value}
        </LinkButton>
      )
    },
    {
      field: 'socialSecurityNumber',
      headerName: 'SSN',
      minWidth: 120,
      sortable: false
    },
    ...(props.advisoryOrganizationId
      ? []
      : [
          {
            field: 'advisoryOrganizationName',
            flex: 1.5,
            headerName: 'Advisory Organization',
            minWidth: 200,
            renderCell: params => (
              <LinkButton
                params={{
                  advisoryOrganizationId: params.row.advisoryOrganizationId
                }}
                to='/organizations/$advisoryOrganizationId'
                variant='text'>
                {params.value}
              </LinkButton>
            )
          } satisfies GridColDef<GetAdminParticipantsRes>
        ]),
    ...(props.planId
      ? []
      : [
          {
            field: 'planDescription',
            flex: 1.5,
            headerName: 'Plan',
            minWidth: 200,
            renderCell: params => (
              <LinkButton
                params={{
                  advisoryOrganizationId: params.row.advisoryOrganizationId,
                  planId: params.row.planId
                }}
                to='/organizations/$advisoryOrganizationId/plans/$planId'
                variant='text'>
                {params.value}
              </LinkButton>
            )
          } satisfies GridColDef<GetAdminParticipantsRes>
        ]),
    { ...NUMERIC, field: 'age', headerName: 'Age', width: 80 },
    {
      field: 'onTrackRate',
      headerName: 'Goal Completion',
      minWidth: 160,
      renderCell: params => (
        <TrackRate
          data-testid={`onTrackRate-${params.row.participantId}`}
          rate={params.value}
        />
      )
    },
    {
      ...NUMERIC,
      field: 'externalTotalPlanBalance',
      headerName: 'Balance',
      minWidth: 130
    },
    {
      ...NUMERIC,
      field: 'outsideAssets',
      headerName: 'Outside Assets',
      minWidth: 140
    },
    { ...NUMERIC, field: 'annualSalary', headerName: 'Salary', minWidth: 120 },
    {
      ...NUMERIC,
      field: 'currentTotalContributionPercent',
      headerName: 'Savings Rate',
      minWidth: 170
    },
    {
      field: 'isMaximizingERMatch',
      headerName: 'Match Maximized',
      minWidth: 150,
      renderCell: params => (
        <StatusBadge
          severity={
            params.value == null ? 'info' : params.value ? 'success' : 'warning'
          }>
          {params.value == null ? 'N/A' : params.value ? 'Yes' : 'No'}
        </StatusBadge>
      )
    },
    /* {
      ...NUMERIC,
      field: 'wealthScore',
      headerName: 'Wealth Score',
      minWidth: 120
    },
    {
      ...NUMERIC,
      field: 'wealthMidpoint',
      headerName: 'Wealth Midpoint',
      minWidth: 150
    }, */
    /* { field: 'wealthPersona', headerName: 'Persona', minWidth: 130 }, */
    { field: 'gender', headerName: 'Gender', minWidth: 100 },
    {
      ...NUMERIC,
      field: 'fundElections',
      headerName: 'Fund Elections',
      minWidth: 130
    },
    {
      ...NUMERIC,
      field: 'currentPreTaxContribution',
      headerName: 'Pre-tax',
      minWidth: 110,
      sortable: false,
      valueGetter: (_, row) =>
        row.currentPreTaxContributionTypeId ===
        INVESTMENT_VEHICLE_CONTRIBUTION_UNIT_TYPES.DOLLARS
          ? row.currentPreTaxContributionDollars
          : row.currentPreTaxContributionPercent
    },
    {
      ...NUMERIC,
      field: 'currentPostTaxContribution',
      headerName: 'Roth',
      minWidth: 110,
      sortable: false,
      valueGetter: (_, row) =>
        row.currentPostTaxContributionTypeId ===
        INVESTMENT_VEHICLE_CONTRIBUTION_UNIT_TYPES.DOLLARS
          ? row.currentPostTaxContributionDollars
          : row.currentPostTaxContributionPercent
    },
    {
      ...NUMERIC,
      field: 'ldiReturnRate',
      headerName: 'Return Rate',
      minWidth: 130
    },
    {
      field: 'entryExitTypeCode',
      headerName: 'MAP Status',
      minWidth: 140,
      renderCell: params => (
        <StatusBadge severity={params.row.inMap ? 'success' : 'info'}>
          {params.value
            ? (MAP_ENTRY_TYPE_LABELS[params.value] ?? params.value)
            : 'N/A'}
        </StatusBadge>
      )
    },
    {
      field: 'mapMethodologyName',
      headerName: 'Methodology',
      minWidth: 180,
      sortable: false
    },
    { field: 'eligibleOn', headerName: 'Eligible On', minWidth: 140 },
    {
      field: 'employmentStatusId',
      headerName: 'Employment Status',
      minWidth: 160,
      renderCell: params =>
        params.row.employmentStatusDesc ? (
          <StatusBadge
            severity={
              EMPLOYMENT_STATUS_SEVERITY[params.row.employmentStatusDesc] ??
              'info'
            }>
            {params.row.employmentStatusDesc}
          </StatusBadge>
        ) : null
    },
    {
      field: 'planParticipationStatusId',
      headerName: 'Plan Status',
      minWidth: 150,
      renderCell: params =>
        params.row.planParticipationStatusDesc ? (
          <StatusBadge
            severity={
              PLAN_STATUS_SEVERITY[params.row.planParticipationStatusDesc] ??
              'info'
            }>
            {params.row.planParticipationStatusDesc}
          </StatusBadge>
        ) : null
    },
    {
      field: 'personalEmail',
      headerName: 'Home Email Address',
      minWidth: 200
    },
    { field: 'workEmail', headerName: 'Work Email Address', minWidth: 200 },
    {
      field: 'workPhone',
      headerName: 'Work Phone',
      minWidth: 150,
      sortable: false
    },
    {
      field: 'cellPhone',
      headerName: 'Cell Phone',
      minWidth: 150,
      sortable: false
    },
    {
      field: 'lastEnrollmentDate',
      headerName: 'Last Enrollment',
      minWidth: 200
    },
    { field: 'lastLogin', headerName: 'Last Login', minWidth: 200 },
    { field: 'lastUpdated', headerName: 'Last Updated', minWidth: 200 },
    {
      field: 'autoEnrolled',
      headerName: 'Auto Enrolled',
      minWidth: 130,
      renderCell: params => (
        <StatusBadge severity={params.value ? 'success' : 'warning'}>
          {params.value ? 'Yes' : 'No'}
        </StatusBadge>
      ),
      sortable: false
    },
    {
      field: 'autoIncrease',
      headerName: 'Auto Increase',
      minWidth: 130,
      renderCell: params => (
        <StatusBadge severity={params.value ? 'success' : 'warning'}>
          {params.value ? 'Yes' : 'No'}
        </StatusBadge>
      ),
      sortable: false
    },
    {
      align: 'center',
      field: 'actions',
      headerName: '',
      renderCell: params => <ParticipantActionsCell row={params.row} />,
      sortable: false,
      width: 65
    }
  ];

  return (
    <Stack sx={{ flex: 1, minHeight: 0, width: '100%' }}>
      <DataGrid
        autoHeight={false}
        columns={columns}
        disableColumnMenu
        disableRowSelectionOnClick
        filterDefs={filters}
        getRowId={row => row.planEnrollmentId}
        initialSort={[{ field: 'lastName', sort: 'asc' }]}
        loading={!query || participants.isFetching}
        onClear={setQuery}
        onSearch={setQuery}
        pageSizeOptions={[10, 25, 50, 100]}
        paginationMode='server'
        pinnedColumns={{
          left: ['lastName', 'firstName', 'socialSecurityNumber'],
          right: ['actions']
        }}
        rowCount={participants.data?.pagination?.total ?? 0}
        rows={participants.data?.data ?? []}
        showColumnMenu
        showToolbar
        slotProps={{
          root: {
            'data-component': 'dataGrid',
            'data-testid': testId
          },
          toolbar: {
            onDraftChange: props.onDraftChange,
            onRefresh: participants.refetch,
            searchButtonTestId: 'search',
            triggerTestId: `${testId}FilterTrigger`
          }
        }}
        slots={{ toolbar: FilterToolbar }}
        sortingMode='server'
        sx={{ flex: 1, minHeight: 0 }}
      />
    </Stack>
  );
};

ParticipantsGrid.displayName = 'ParticipantsGrid';
